// Hilfe: Themen aus hilfe/inhalt/<id>.md (Kopf mit titel, gruppe, reihenfolge, stichworte), Volltextsuche und das Thema
// passend zur aktuellen Ansicht. Ohne React, damit es sich testen lässt.

export interface Thema {
  id: string;
  titel: string;
  gruppe: string;
  reihenfolge: number;
  stichworte: string[];
  md: string;
  /** Text ohne Markdown-Zeichen, für Suche und Ausschnitte */
  text: string;
}

/** Reihenfolge der Gruppen in der Themenliste; unbekannte Gruppen danach. */
export const GRUPPEN = ['Grundlagen', 'Prozesse', 'Projektsteuerung', 'Verwaltung'];
export const START = 'erste-schritte';

/** Markdown-Datei mit Kopfzeilen zwischen --- in ein Thema zerlegen. */
export function themaAus(id: string, roh: string): Thema {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(roh);
  const kopf: Record<string, string> = {};
  if (m) for (const z of m[1].split(/\r?\n/)) {
    const i = z.indexOf(':');
    if (i > 0) kopf[z.slice(0, i).trim()] = z.slice(i + 1).trim();
  }
  const md = (m ? m[2] : roh).trim();
  return {
    id,
    titel: kopf.titel || id,
    gruppe: kopf.gruppe || 'Weitere',
    reihenfolge: Number(kopf.reihenfolge) || 999,
    stichworte: (kopf.stichworte || '').split(',').map((s) => s.trim()).filter(Boolean),
    md,
    text: klartext(md),
  };
}

/** Markdown grob in Fließtext: Links, Hervorhebungen, Tabellenstriche und Überschriftenzeichen entfernen. */
export function klartext(md: string): string {
  return md
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s*\|?[\s:|-]+\|[\s:|-]*$/gm, ' ')
    .replace(/[*_`>#|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Themen nach Gruppe und Reihenfolge sortiert. */
export function sortieren(themen: Thema[]): Thema[] {
  const g = (t: Thema) => { const i = GRUPPEN.indexOf(t.gruppe); return i < 0 ? GRUPPEN.length : i; };
  return [...themen].sort((a, b) => g(a) - g(b) || a.reihenfolge - b.reihenfolge || a.titel.localeCompare(b.titel));
}

/** Für die Suche: klein, Umlaute und ß vereinheitlicht. */
export const norm = (s: string) => s.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');

export interface Treffer { thema: Thema; ausschnitt: string; punkte: number }

/**
 * Volltextsuche: Jedes Wort der Anfrage muss in Titel, Stichworten oder Text vorkommen.
 * Gewichtung: Titel vor Stichworten vor Text; Ausschnitt um die erste Fundstelle im Text.
 */
export function suchen(themen: Thema[], anfrage: string): Treffer[] {
  const woerter = norm(anfrage).split(/\s+/).filter((w) => w.length > 1);
  if (woerter.length === 0) return [];
  const l: Treffer[] = [];
  for (const t of themen) {
    const titel = norm(t.titel), stich = norm(t.stichworte.join(' ')), text = norm(t.text);
    let punkte = 0;
    let alle = true;
    for (const w of woerter) {
      const p = (titel.includes(w) ? 20 : 0) + (stich.includes(w) ? 8 : 0) + Math.min(text.split(w).length - 1, 10);
      if (p === 0) { alle = false; break; }
      punkte += p;
    }
    if (alle) l.push({ thema: t, punkte, ausschnitt: ausschnitt(t.text, woerter) });
  }
  return l.sort((a, b) => b.punkte - a.punkte);
}

/** Etwa 140 Zeichen um die erste Fundstelle (Positionen in norm() und Original stimmen überein, ß ausgenommen). */
export function ausschnitt(text: string, woerter: string[]): string {
  const n = norm(text);
  const pos = woerter.map((w) => n.indexOf(w)).filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (pos === undefined) return text.slice(0, 140) + (text.length > 140 ? ' …' : '');
  const von = Math.max(0, pos - 50);
  const bis = Math.min(text.length, pos + 90);
  return (von > 0 ? '… ' : '') + text.slice(von, bis).trim() + (bis < text.length ? ' …' : '');
}

/** Was gerade zu sehen ist – daraus ergibt sich das Thema der Hilfe. */
export interface Kontext {
  view?: string;
  dialog?: string | null;
  bearbeiten?: boolean;
  auswahl?: boolean;
}

const ANSICHT: Record<string, string> = {
  general: 'allgemeines', map: 'landkarte', decisions: 'entscheidungen', tasks: 'aufgaben', milestones: 'meilensteine',
  issues: 'offene-punkte', cockpit: 'cockpit', abnahmen: 'abnahmen', stand: 'speichern', tests: 'tests', checklisten: 'checklisten', dokumente: 'dokumente',
};
const DIALOG: Record<string, string> = {
  settings: 'einstellungen', mitglieder: 'mitglieder', protokoll: 'protokoll', history: 'speichern',
};

/** Thema zur aktuellen Ansicht; ohne Treffer die Einstiegsseite. */
export function themaFuer(k: Kontext): string {
  if (k.dialog && DIALOG[k.dialog]) return DIALOG[k.dialog];
  if (k.view === 'process') return k.bearbeiten ? 'bearbeiten' : k.auswahl ? 'detailbereich' : 'diagramm';
  return (k.view && ANSICHT[k.view]) || START;
}
